import { TrackerPosition, Vehicle } from "@/interfaces";
import { format } from "date-fns";
import L from "leaflet";
import "leaflet-rotatedmarker";
import { Power } from "lucide-react";
import React from "react";
import { Marker, Popup } from "react-leaflet";
import StartFinishMarker from "./StartFinishMarker";

interface RouteMarkerProps {
  positions: TrackerPosition[];
  showIcons: boolean;
  vehicles: Vehicle[] | undefined;
}

const movingIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`
    <svg width="38" height="38" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 12 L25 30 L19 25 L13 30 Z" fill="#28A745" stroke="#000" stroke-width="1"/>
    </svg>
  `)}`,
  iconSize: [38, 38],
  iconAnchor: [19, 18],
});

const stoppedIgnitionOnIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`
    <svg width="38" height="38" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 12 L25 30 L19 25 L13 30 Z" fill="#FFA500" stroke="#000" stroke-width="1"/>
    </svg>
  `)}`,
  iconSize: [38, 38],
  iconAnchor: [19, 18],
});

const turnedOffIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`
    <svg width="38" height="38" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 12 L25 30 L19 25 L13 30 Z" fill="#ef4444" stroke="#000" stroke-width="1"/>
    </svg>
  `)}`,
  iconSize: [38, 38],
  iconAnchor: [19, 18],
});

const getIcon = (speed: number, ignition: number) => {
  if (speed > 0) return movingIcon;
  if (ignition) return stoppedIgnitionOnIcon;
  return turnedOffIcon;
};

const getHeading = (direction: number) => {
  if (direction >= 0 && direction <= 7) {
    return direction * 45;
  }
  return 360;
};

const RouteMarkers: React.FC<RouteMarkerProps> = React.memo(
  ({ positions, showIcons, vehicles }) => {
    return (
      <>
        {positions.map((point, index) => {
          const isStart = index === 0;
          const isEnd = index === positions.length - 1;
          const driverName = point.driver?.name || "";
          const heading = getHeading(point.direction);

          if (isStart || (isEnd && !vehicles)) {
            return (
              <StartFinishMarker
                positionData={point}
                isStart={isStart}
                key={`marker-${index}`}
              />
            );
          } else {
            return (
              <React.Fragment key={`marker-group-${index}`}>
                {showIcons && (
                  <Marker
                    position={[point.latitude, point.longitude]}
                    key={`marker-${index}`}
                    rotationAngle={heading} // Apply rotation in degrees
                    rotationOrigin="center center" // Rotate around the center
                    icon={getIcon(point.speed, point.ignition)}
                  >
                    <Popup>
                      <div className="text-sm p-4 w-64">
                        <div className="mb-1 text-black">
                          Data:{" "}
                          {format(new Date(point.date), "dd/MM/yyyy HH:mm:ss")}
                        </div>
                        <div className="mb-1 text-black flex items-center">
                          Ignição: {point.ignition ? "Ligada" : "Desligada"}
                          <Power
                            className={`ml-1 ${point.ignition ? "text-green-500" : "text-red-500"}`}
                          />
                        </div>
                        <div className="mb-1 text-black">
                          Velocidade: {point.speed}km/h
                        </div>
                        {driverName && (
                          <div className="mb-1 text-black">
                            Condutor: {driverName}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                )}
              </React.Fragment>
            );
          }
        })}
      </>
    );
  },
);

export default RouteMarkers;
